import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Heading,
  Button,
  TextInput,
  TextArea,
  Radio,
  RadioGroup,
  Divider,
  CheckCircleIcon,
  MailIcon,
  GlobeIcon,
  PlusIcon,
  TrashIcon,
  IconButton,
} from '@razorpay/blade/components';
import { ActionConfig, ApiHeader } from 'api/types/aiWorkflowTypes';
import { ACTION_ICONS, HTTP_METHODS } from '../../constants';
import { isAbsoluteUrl } from 'utils/Urls';
import { PlaceholdersPopover } from './PlaceholdersPopover';
import { ActionConfigurationProps } from './types';

const ApproveRejectConfig = () => (
  <Box display="flex" flexDirection="column" gap="spacing.4">
    <Box
      padding="spacing.4"
      backgroundColor="surface.background.gray.moderate"
      borderRadius="medium">
      <Text size="small" color="surface.text.gray.muted">
        This action will automatically approve or reject the request based on the AI decision.
        If the decision is true, the request will be approved. Otherwise, it will be rejected.
      </Text>
    </Box>
  </Box>
);

const EmailConfig = ({
  localConfig,
  errors,
  handleChange,
}: {
  localConfig: ActionConfig;
  errors: Record<string, string>;
  handleChange: (key: keyof ActionConfig, value: unknown) => void;
}) => (
  <Box display="flex" flexDirection="column" gap="spacing.4" padding="1px">
    <TextInput
      label="Subject*"
      placeholder="Email subject..."
      value={localConfig.emailSubject || ''}
      onChange={({ value }) => handleChange('emailSubject', value)}
      validationState={errors.emailSubject ? 'error' : undefined}
      errorText={errors.emailSubject}
    />
    <TextArea
      label="Message*"
      placeholder="Email body... Use placeholders like {{employee_name}}, {{decision}}, etc."
      value={localConfig.content || ''}
      onChange={({ value }) => handleChange('content', value)}
      numberOfLines={4}
      validationState={errors.content ? 'error' : undefined}
      errorText={errors.content}
    />
    <PlaceholdersPopover actionType="send_email" />
    <Text size="small" color="surface.text.gray.muted">
      The email will be sent to the workflow creator with placeholders replaced by actual values.
    </Text>
  </Box>
);

const ApiConfig = ({
  localConfig,
  errors,
  handleChange,
}: {
  localConfig: ActionConfig;
  errors: Record<string, string>;
  handleChange: (key: keyof ActionConfig, value: unknown) => void;
}) => {
  const headers: ApiHeader[] = localConfig.headers || [];

  const handleAddHeader = () => {
    handleChange('headers', [...headers, { key: '', value: '' }]);
  };

  const handleRemoveHeader = (index: number) => {
    handleChange('headers', headers.filter((_, i) => i !== index));
  };

  const handleHeaderChange = (index: number, field: 'key' | 'value', value: string) => {
    const newHeaders = headers.map((header, i) => 
      i === index ? { ...header, [field]: value } : header
    );
    handleChange('headers', newHeaders);
  };

  return (
    <Box display="flex" flexDirection="column" gap="spacing.4" padding="1px">
      <TextInput
        label="API URL*"
        placeholder="https://api.example.com/webhook"
        value={localConfig.url || ''}
        onChange={({ value }) => handleChange('url', value)}
        validationState={errors.url ? 'error' : undefined}
        errorText={errors.url}
      />
      <RadioGroup
        label="HTTP Method"
        value={localConfig.method || 'POST'}
        onChange={({ value }) => handleChange('method', value)}>
        {HTTP_METHODS.map((method) => (
          <Radio key={method} value={method}>
            {method}
          </Radio>
        ))}
      </RadioGroup>

      <Box display="flex" flexDirection="column" gap="spacing.3">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Text weight="semibold" size="medium">Headers (Optional)</Text>
          <Button
            variant="tertiary"
            size="small"
            icon={PlusIcon}
            onClick={handleAddHeader}>
            Add Header
          </Button>
        </Box>
        
        {headers.length > 0 && (
          <Box display="flex" flexDirection="column" gap="spacing.3">
            {headers.map((header, index) => (
              <Box key={index} display="flex" gap="spacing.3" alignItems="flex-start">
                <Box flex="1">
                  <TextInput
                    label=""
                    placeholder="Header name (e.g., Authorization)"
                    value={header.key}
                    onChange={({ value }) => handleHeaderChange(index, 'key', value || '')}
                    size="medium"
                  />
                </Box>
                <Box flex="1">
                  <TextInput
                    label=""
                    placeholder="Header value"
                    value={header.value}
                    onChange={({ value }) => handleHeaderChange(index, 'value', value || '')}
                    size="medium"
                  />
                </Box>
                <IconButton
                  icon={TrashIcon}
                  accessibilityLabel="Remove header"
                  onClick={() => handleRemoveHeader(index)}
                  emphasis="subtle"
                  size="medium"
                />
              </Box>
            ))}
          </Box>
        )}
        
        {headers.length === 0 && (
          <Box
            padding="spacing.3"
            backgroundColor="surface.background.gray.moderate"
            borderRadius="medium">
            <Text size="small" color="surface.text.gray.muted">
              No headers added. Click "Add Header" to include custom HTTP headers.
            </Text>
          </Box>
        )}
      </Box>

      <TextArea
        label="Request payload (JSON)"
        placeholder='{"employee": "{{employee_name}}", "decision": "{{decision}}"}'
        value={localConfig.payload || ''}
        onChange={({ value }) => handleChange('payload', value)}
        numberOfLines={4}
        validationState={errors.payload ? 'error' : undefined}
        errorText={errors.payload}
      />
      <PlaceholdersPopover actionType="call_api" />
      <Text size="small" color="surface.text.gray.muted">
        The API will be called with placeholders replaced by actual values. Leave empty for default payload.
      </Text>
    </Box>
  );
};

const DefaultConfig = () => (
  <Box
    padding="spacing.4"
    backgroundColor="surface.background.gray.moderate"
    borderRadius="medium">
    <Text size="small" color="surface.text.gray.muted">
      Configuration for this action type is coming soon.
    </Text>
  </Box>
);

const ActionConfiguration = ({
  action,
  config,
  onSave,
}: ActionConfigurationProps) => {
  const [localConfig, setLocalConfig] = useState<ActionConfig>(config || {});
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setLocalConfig(config || {});
  }, [config]);

  const handleChange = (key: keyof ActionConfig, value: unknown) => {
    const newConfig = { ...localConfig, [key]: value };
    setLocalConfig(newConfig);
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const validateConfig = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (action.code === 'send_email') {
      if (!localConfig.emailSubject?.trim()) {
        newErrors.emailSubject = 'Subject is required';
      }
      if (!localConfig.content?.trim()) {
        newErrors.content = 'Message content is required';
      }
    } else if (action.code === 'call_api') {
      if (!localConfig.url?.trim()) {
        newErrors.url = 'API URL is required';
      } else if (!isAbsoluteUrl(localConfig.url)) {
        newErrors.url = 'Please enter a valid URL';
      }
      if (localConfig.payload?.trim()) {
        try {
          JSON.parse(localConfig.payload);
        } catch {
          newErrors.payload = 'Invalid JSON format';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateConfig()) {
      onSave(localConfig);
    }
  };

  const getIcon = () => {
    switch (action.code) {
      case 'approve_or_reject':
        return <CheckCircleIcon size="medium" color="feedback.icon.positive.intense" />;
      case 'send_email':
        return <MailIcon size="medium" color="interactive.icon.primary.normal" />;
      case 'call_api':
        return <GlobeIcon size="medium" color="interactive.icon.primary.normal" />;
      default:
        return <Text size="medium">{ACTION_ICONS[action.code] || '⚡'}</Text>;
    }
  };

  const renderConfigFields = () => {
    if (action.code === 'approve_or_reject') {
      return <ApproveRejectConfig />;
    }
    if (action.code === 'send_email') {
      return <EmailConfig localConfig={localConfig} errors={errors} handleChange={handleChange} />;
    }
    if (action.code === 'call_api') {
      return <ApiConfig localConfig={localConfig} errors={errors} handleChange={handleChange} />;
    }
    return <DefaultConfig />;
  };

  return (
    <Box display="flex" flexDirection="column" gap="spacing.5">
      <Box display="flex" alignItems="center" gap="spacing.3">
        {getIcon()}
        <Heading size="medium">{action.name}</Heading>
      </Box>

      <Text color="surface.text.gray.muted">
        Configure the {action.name.toLowerCase()} action
      </Text>

      <Divider />

      {renderConfigFields()}

      <Box marginTop="spacing.4">
        <Button onClick={handleSave} isFullWidth>
          Save Action
        </Button>
      </Box>
    </Box>
  );
};

export { ActionConfiguration };
